'use client';
import { ReactNode } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

import { TableSkeleton } from '@/components';
import { useT } from '@/hooks';
import { TableError } from './TableError';

interface Column<T> {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, item: T) => ReactNode;
  width?: string | number;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  emptyMessage?: string;
  maxHeight?: string | number;
}

function HeaderCell<T>({ column }: { column: Column<T> }) {
  const cellStyle = {
    fontWeight: 600,
    width: column.width,
    bgcolor: 'background.paper',
  };

  return (
    <TableCell align={column.align || 'left'} sx={cellStyle}>
      {column.label}
    </TableCell>
  );
}

function DataTableHeader<T>({ columns }: { columns: Column<T>[] }) {
  const renderHeaderCell = (column: Column<T>) => <HeaderCell key={column.id} column={column} />;

  const headerCells = columns.map(renderHeaderCell);

  return (
    <TableHead>
      <TableRow>{headerCells}</TableRow>
    </TableHead>
  );
}

interface DataTableRowCellProps<T> {
  item: T;
  column: Column<T>;
}

function DataTableRowCell<T>({ item, column }: DataTableRowCellProps<T>) {
  const value = (item as any)[column.id];
  const cellContent = column.render ? column.render(value, item) : value;

  return <TableCell align={column.align || 'left'}>{cellContent}</TableCell>;
}

interface DataTableRowProps<T> {
  item: T;
  columns: Column<T>[];
}

function DataTableRow<T extends { id: string | number }>({ item, columns }: DataTableRowProps<T>) {
  const renderCell = (column: Column<T>) => (
    <DataTableRowCell key={`${item.id}-${column.id}`} item={item} column={column} />
  );

  const cells = columns.map(renderCell);

  return <TableRow hover>{cells}</TableRow>;
}

export function DataTable<T extends { id: string | number }>({
  columns,
  data,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  error = null,
  onRetry,
  emptyMessage: customEmptyMessage,
  maxHeight = 'calc(100vh - 380px)',
}: DataTableProps<T>) {
  const { t } = useT();
  const emptyMessage =
    customEmptyMessage || t('common.noData', { defaultValue: 'No data available.' });

  const handlePaginationChange = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newLimit = parseInt(e.target.value, 10);
    onRowsPerPageChange(newLimit);
  };

  if (loading) {
    const skeleton = (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box sx={{ height: maxHeight }}>
          <TableSkeleton columns={columns.length} rows={rowsPerPage} />
        </Box>
        {/* Pagination placeholder — mirrors the real TablePagination bar below the table */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 3,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderLeft: '1px solid',
            borderRight: '1px solid',
            borderColor: 'divider',
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            bgcolor: 'background.paper',
          }}
        >
          <Skeleton variant="text" width={110} height={20} />
          <Skeleton variant="text" width={70} height={20} />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Skeleton variant="circular" width={28} height={28} />
            <Skeleton variant="circular" width={28} height={28} />
          </Box>
        </Box>
      </Box>
    );
    return skeleton;
  }

  if (error) {
    const errorDisplay = (
      <Box
        sx={{
          height: maxHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <TableError message={error} onRetry={onRetry} />
      </Box>
    );
    return errorDisplay;
  }

  const renderRow = (item: T) => <DataTableRow key={item.id} item={item} columns={columns} />;

  const rows = data.map(renderRow);

  const emptyDisplay = (
    <TableRow>
      <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
        {emptyMessage}
      </TableCell>
    </TableRow>
  );

  const bodyContent = data.length === 0 ? emptyDisplay : rows;

  const containerStyle = {
    border: '1px solid',
    borderColor: 'divider',
    maxHeight: maxHeight,
    overflow: 'auto',
    borderRadius: 1,
    flex: maxHeight === '100%' ? 1 : 'initial',
  };

  const paginationStyle = {
    borderBottom: '1px solid',
    borderLeft: '1px solid',
    borderRight: '1px solid',
    borderColor: 'divider',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    bgcolor: 'background.paper',
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <TableContainer component={Paper} elevation={0} sx={containerStyle}>
        <Table sx={{ minWidth: 650 }} stickyHeader aria-label="data table">
          <DataTableHeader columns={columns} />
          <TableBody>{bodyContent}</TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handlePaginationChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={paginationStyle}
      />
    </Box>
  );
}
